'use client';

import {
  useAutoActivateVoucherFromUrl,
  useAutoApplyActiveCoupons,
  useAutoApplySingleCoupon,
  useClearLeftoverActiveCoupons,
} from '@/hooks/useCartHooks';
import { useIncludePromoCoupons } from '@/hooks/useIncludePromoCoupons';
import { useIsMultiCouponEnabled } from '@/hooks/useIsMultiCouponEnabled';

type CouponApplierProps = { includePromoCoupons: boolean };

// multi-coupon variant: validates all active coupons with pricing-service, applies the valid ones
function ValidatedCouponsApplier({ includePromoCoupons }: CouponApplierProps) {
  useAutoApplyActiveCoupons({ includePromoCoupons });
  return null;
}

// single-coupon control: applies the single active coupon, as before the A/B test
function SingleCouponApplier({ includePromoCoupons }: CouponApplierProps) {
  useClearLeftoverActiveCoupons();
  useAutoApplySingleCoupon({ includePromoCoupons });
  return null;
}

/**
 * Activates a coupon passed via the `?couponCode=` URL param for the duration of the session
 * and posts the active coupons to the cart. Rendered globally (independent of whether a CMS promo
 * banner is shown) so coupon links work on every page; it is the only place coupons get applied.
 *
 * URL coupons are applied everywhere. Promo coupons only where the promo banner is shown — never
 * on routes that hide it (e.g. TOTW). The multi-coupon A/B test picks the applier; only the
 * rendered one runs its hooks and requests.
 */
export function UrlCouponHandler() {
  const includePromoCoupons = useIncludePromoCoupons();
  const isMultiCouponEnabled = useIsMultiCouponEnabled();

  useAutoActivateVoucherFromUrl();

  return isMultiCouponEnabled ? (
    <ValidatedCouponsApplier includePromoCoupons={includePromoCoupons} />
  ) : (
    <SingleCouponApplier includePromoCoupons={includePromoCoupons} />
  );
}
